import {
  useFetchConnectedIntegrations,
  useUpdateIntegration,
} from '@modules/integrationsV2/hooks';
import { Box } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationChannelViewFragment,
  IntegrationType,
  UpdateIntegrationData,
} from 'generated/graphql';

interface RemoveIntegrationChannelOptions {
  integrationID: string;
  integrationChannel: IntegrationChannelViewFragment;
  integrationType: IntegrationType;
  channelID: string;
  onCompleted: () => void;
  channelName: string;
}

export const useRemoveIntegrationChannel = ({
  integrationID,
  integrationChannel,
  integrationType,
  channelID,
  channelName,
  onCompleted,
}: RemoveIntegrationChannelOptions) => {
  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first', // We already have the data in the cache, so we don't need to refetch it
  });

  const integrationData = connectedIntegrations?.find(
    (integration) => integration.id === integrationID,
  )?.data;

  const [updateIntegration, { status }] = useUpdateIntegration({
    channelType: integrationChannel.type,
    integrationType,
    onCompleted,
    successNotificationContentNode: (
      <Box>
        {integrationChannel.displayName} integration{' '}
        <strong>{channelName}</strong> has been removed successfully.
      </Box>
    ),
  });

  const removeIntegrationChannel = async () => {
    if (!integrationData) {
      return;
    }

    const updatedIntegrationData: UpdateIntegrationData = {};

    if (
      integrationChannel.type === IntegrationChannelType.Slack &&
      integrationData.slack?.subscribedChannels
    ) {
      updatedIntegrationData.slack = {
        subscribedChannels: integrationData.slack.subscribedChannels.filter(
          (channel) => channel.id !== channelID,
        ),
      };
    }

    if (
      integrationChannel.type === IntegrationChannelType.Discord &&
      integrationData.discord?.subscribedChannels
    ) {
      updatedIntegrationData.discord = {
        subscribedChannels: integrationData.discord.subscribedChannels.filter(
          (channel) => channel.id !== channelID,
        ),
      };
    }

    if (
      integrationChannel.type === IntegrationChannelType.SurveyMonkey &&
      integrationData.surveyMonkey?.includedSurveys
    ) {
      updatedIntegrationData.surveyMonkey = {
        includedSurveys: integrationData.surveyMonkey.includedSurveys.filter(
          (survey) => survey.surveyID !== channelID,
        ),
      };
    }

    if (
      integrationChannel.type === IntegrationChannelType.Typeform &&
      integrationData.typeform?.includedForms
    ) {
      updatedIntegrationData.typeform = {
        includedForms: integrationData.typeform.includedForms.filter(
          (form) => form.id !== channelID,
        ),
      };
    }

    await updateIntegration({
      integrationID,
      isActive: true,
      updateData: updatedIntegrationData,
    });
  };

  return { removeIntegrationChannel, status };
};
